import { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Check if user is logged in on mount
        const token = localStorage.getItem('auth_token');
        const savedUser = localStorage.getItem('user');

        if (token && savedUser) {
            setUser(JSON.parse(savedUser));
            fetchUser(); // Verify token is still valid
        } else {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchUser = async () => {
        try {
            const response = await api.get('/user');
            setUser(response.data);
            localStorage.setItem('user', JSON.stringify(response.data));
        } catch (error) {
            // The cached session failed validation. Clear it SYNCHRONOUSLY, right here,
            // rather than calling the async logout().
            //
            // The bug that taught us this: logout() is async and was called without
            // being awaited, so `finally { setLoading(false) }` ran while logout() was
            // still waiting on its POST /logout. For that window `loading` was false
            // and `user` was still the stale value read from localStorage — which is
            // all PublicRoute needs to decide you are signed in and redirect you to
            // /feed. A visitor with an expired token therefore got bounced off the
            // public landing page to /feed, and from there to /login, never seeing the
            // page at all. The redirect looked like it came from the 401 handler; it
            // actually came from this race.
            //
            // There is also nothing to tell the server: the token it would revoke has
            // already been rejected, so POST /logout would only 401 again.
            localStorage.removeItem('auth_token');
            localStorage.removeItem('user');
            setUser(null);
        } finally {
            setLoading(false);
        }
    };

    const login = async (email, password) => {
        const response = await api.post('/login', { email, password });
        const { user, token } = response.data;

        localStorage.setItem('auth_token', token);
        localStorage.setItem('user', JSON.stringify(user));
        setUser(user);

        return user;
    };

    const register = async (name, email, password, password_confirmation, birthday) => {
        const response = await api.post('/register', {
            name,
            email,
            password,
            password_confirmation,
            birthday,
        });
        const { user, token } = response.data;

        localStorage.setItem('auth_token', token);
        localStorage.setItem('user', JSON.stringify(user));
        setUser(user);  // Make sure this line is here!

        return user;
    };

    // Replace the cached user after a profile/avatar update.
    const updateUser = (updatedUser) => {
        setUser(updatedUser);
        localStorage.setItem('user', JSON.stringify(updatedUser));
    };

    const logout = async () => {
        try {
            await api.post('/logout');
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            localStorage.removeItem('auth_token');
            localStorage.removeItem('user');
            setUser(null);
        }
    };

    return (
        <AuthContext.Provider value={{ user, login, register, logout, updateUser, loading }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};